import { useEffect, useState, useMemo } from 'react';
import { Filter, Search } from 'lucide-react';
import {
  DataTable, EmptyState, FilterBuilder, Pagination, StatusPill,
  type ColumnDef, type FilterCondition, type FilterField,
} from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { Badge, Button, Input, Popover, PopoverContent, PopoverTrigger } from '@vnetwork/ui/primitives';
import { Toolbar } from '@vnetwork/ui/layout';
import { formatDateTime, formatMoney } from '@/lib/format';
import { useAffiliatePendingInvoices, type AffiliatePendingInvoice } from '@/api/affiliate';
import { paginationView } from '@/pages/customers/pagination-summary';
import { TableLink } from '@/components/table-link';
import { EntityLink } from '@/components/entity-link';
import { invoiceTone, humanize } from './status-tones';

const PAGE_SIZE = 20;

interface PendingInvoicesTabProps {
  affiliateId: number;
}

const INVOICE_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'waiting-confirm', label: 'Waiting confirm' },
  { value: 'paid', label: 'Paid' },
  { value: 'done', label: 'Done' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'rejected', label: 'Rejected' },
];

const FILTER_FIELDS: FilterField[] = [
  { value: 'status', label: 'Invoice Status', input: 'select', options: INVOICE_STATUS_OPTIONS },
];

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

/**
 * Pending invoices tab — invoices awaiting commission confirmation with
 * status filter and search bar (both threaded to the backend via
 * `useAffiliatePendingInvoices` params).
 */
export function PendingInvoicesTab({ affiliateId }: PendingInvoicesTabProps) {
  const [page, setPage] = useState(1);
  const [rawQuery, setRawQuery] = useState('');
  const query = useDebouncedValue(rawQuery, 400);
  const [filterOpen, setFilterOpen] = useState(false);
  const [conditions, setConditions] = useState<FilterCondition[]>([]);

  const statusFilter = conditions.find((c) => c.field === 'status')?.value;

  const { data, isLoading, isError, refetch } = useAffiliatePendingInvoices(affiliateId, {
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
    ...(statusFilter ? { status: statusFilter } : {}),
    ...(query ? { search: query } : {}),
  });

  const rows = data?.results ?? [];
  const total = data?.pagination?.total ?? rows.length;
  const pageCount = data?.pagination?.total_pages ?? 1;

  const columns = useMemo<ColumnDef<AffiliatePendingInvoice, unknown>[]>(
    () => [
      {
        id: 'invoice_id',
        header: 'Invoice',
        cell: ({ row }) => (
          <TableLink to={`/invoices/${row.original.invoice_id}`}>
            {row.original.invoice_id}
          </TableLink>
        ),
      },
      {
        id: 'customer_name',
        header: 'Customer',
        cell: ({ row }) => (
          <EntityLink
            to={`/customers/${row.original.customer_id}`}
            id={row.original.customer_id}
            name={row.original.customer_name}
          />
        ),
      },
      {
        id: 'invoice_amount',
        header: () => <div style={{ textAlign: 'right' }}>Amount</div>,
        cell: ({ row }) => (
          <div style={{ textAlign: 'right' }}>{formatMoney(row.original.invoice_amount)}</div>
        ),
      },
      {
        id: 'invoice_status',
        header: 'Invoice Status',
        cell: ({ row }) => (
          <StatusPill tone={invoiceTone(row.original.invoice_status)}>
            {humanize(row.original.invoice_status)}
          </StatusPill>
        ),
      },
      {
        id: 'commission_percent',
        header: 'Rate',
        cell: ({ row }) => `${row.original.commission_percent}%`,
      },
      {
        id: 'expected_net_commission',
        header: () => <div style={{ textAlign: 'right' }}>Expected Commission</div>,
        cell: ({ row }) => (
          <div style={{ textAlign: 'right', fontWeight: 'var(--vn-font-weight-semibold)' }}>
            {formatMoney(row.original.expected_net_commission)}
          </div>
        ),
      },
      {
        id: 'invoice_created_at',
        header: 'Created',
        cell: ({ row }) => formatDateTime(row.original.invoice_created_at),
      },
    ],
    [],
  );

  const appliedCount = new Set(conditions.map((c) => c.field)).size;

  const applyFilters = (next: FilterCondition[]) => {
    setConditions(next.filter((c) => c.value !== ''));
    setPage(1);
    setFilterOpen(false);
  };

  if (isError) {
    return (
      <Alert
        tone="danger"
        title="Failed to load pending invoices"
        actions={
          <Button size="sm" variant="ghost" onClick={() => refetch()}>
            Retry
          </Button>
        }
      />
    );
  }

  const view = paginationView(page, PAGE_SIZE, rows.length, total, pageCount);

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <Toolbar
        style={{ marginBottom: 0 }}
        start={
          <Input
            value={rawQuery}
            onChange={(e) => {
              setRawQuery(e.target.value);
              setPage(1);
            }}
            placeholder="Search invoices…"
            startSlot={<Search size={15} />}
            style={{ maxWidth: 320 }}
          />
        }
        end={
          <Popover open={filterOpen} onOpenChange={setFilterOpen}>
            <PopoverTrigger asChild>
              <Button variant="secondary" startIcon={<Filter size={15} />}>
                Filters{appliedCount ? <Badge tone="primary">{appliedCount}</Badge> : null}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" style={{ padding: 0 }}>
              <FilterBuilder
                fields={FILTER_FIELDS}
                defaultValue={conditions.length ? conditions : undefined}
                onApply={applyFilters}
                onClose={() => setFilterOpen(false)}
              />
            </PopoverContent>
          </Popover>
        }
      />

      <DataTable
        columns={columns}
        data={rows}
        enableSorting={false}
        getRowId={(r) => String(r.invoice_id)}
        emptyState={
          <EmptyState
            title="No pending invoices"
            description="Invoices awaiting commission confirmation will appear here."
          />
        }
        footer={
          isLoading ? null : (
            <Pagination
              page={page}
              pageCount={view.pageCount}
              onPageChange={setPage}
              summary={view.summary}
            />
          )
        }
      />
    </div>
  );
}
